import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useVisualViewport } from '../../lib/viewport';
import { LanguageToggle } from '../LanguageToggle';
import { BrandMark } from './BrandMark';
import { PanelEntrance } from './PanelEntrance';
import { VvDebug } from '../VvDebug';
import { Wordmark } from './Wordmark';

/**
 * Auth frame.
 * - Phones: a fixed root sized from the visual viewport (lib/viewport.ts, touch devices), so
 *   the header stays put when a field is focused and the keyboard opens; the form area is the
 *   only scroller. A compact header with the wordmark and the language toggle (language lives
 *   here and on onboarding only; inside the app it is under You → Your account).
 * - Large screens (M5.2): a centred max-w-6xl container with the brand panel (5/12) beside the
 *   card (7/12); the two panels share one top edge and one height (stretch). Panel: soft blue
 *   with ink text in light mode; in dark mode the elevated surface with a low Deep TWO tint.
 *   The panel keeps the mark, the hero question and the closing line; no large heading.
 */
export function AuthLayout({ children, footer }: { children: ReactNode; footer?: ReactNode }) {
  const { t } = useTranslation();
  useVisualViewport();
  return (
    <div className="vv-root bg-canvas text-ink">
      <VvDebug />
      <header className="flex shrink-0 items-center justify-between px-5 pt-safe pb-2 pt-4 lg:mx-auto lg:w-full lg:max-w-6xl lg:px-8 lg:pt-6">
        <div className="flex items-center gap-2.5" dir="ltr">
          <BrandMark size={24} />
          <Wordmark />
        </div>
        <LanguageToggle />
      </header>

      <div className="scroller flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-full flex-1 flex-col lg:items-center lg:justify-center lg:px-8 lg:py-10">
          <div className="flex flex-1 flex-col lg:mx-auto lg:grid lg:w-full lg:max-w-6xl lg:flex-none lg:grid-cols-12 lg:items-stretch lg:gap-10">
            <aside
              data-auth-panel
              className="hidden rounded-xl border border-line bg-panel p-10 text-ink lg:col-span-5 lg:flex lg:min-h-[32rem] lg:flex-col lg:justify-between"
            >
              <BrandMark size={32} />
              <PanelEntrance question={t('brand.question')} answer={t('brand.answer')} />
              <p className="flex items-baseline gap-2 text-sm text-ink-2">
                <Wordmark size="sm" />
                <span>{t('brand.tagline')}</span>
              </p>
            </aside>

            <main
              data-auth-card
              className="flex flex-1 flex-col px-5 pb-6 lg:col-span-7 lg:flex-none lg:p-0"
            >
              <div className="mx-auto flex w-full max-w-md flex-1 flex-col lg:h-full lg:max-w-none lg:rounded-xl lg:border lg:border-line lg:bg-surface lg:p-10 lg:shadow-sm">
                {children}
              </div>
            </main>
          </div>
        </div>

        {footer ? (
          <footer className="px-5 pb-safe pb-4 text-center text-xs text-ink-3 lg:mx-auto lg:w-full lg:max-w-6xl lg:px-8">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
